import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Grid } from '@mui/material';

import { Box } from 'styles/layout';
import FormWrapper from 'shared/components/forms/FormWrapper/FormWrapper';
import InputField from 'shared/components/forms/InputField/InputField';
import DatePicker from 'shared/components/forms/DatePicker/DatePicker';
import { useFormState } from 'shared/hooks';

import { schema, mapForm2Api, FormFields } from './utils';

const Hl7OrderForm = ({
  onSubmit,
  onCancel,
  initValues = null,
  disabled = false,
  hideButtons = false,
}) => {
  const isEditMode = Boolean(initValues?.id);
  const {
    register,
    control,
    handleSubmit,
    getFieldError,
    setFormValues,
    canSubmitForm,
  } = useFormState(schema, { stateSlice: 'admin', showAsSnackbar: true });

  useEffect(() => {
    if (!initValues) return;
    setFormValues(initValues);
  }, [initValues, setFormValues]);

  const onFormSubmit = React.useCallback(
    (submitData) => {
      onSubmit(mapForm2Api(submitData));
    },
    [onSubmit],
  );

  return (
    <FormWrapper
      formId="hl7OrderForm"
      hideButtons={hideButtons}
      onSubmit={handleSubmit(onFormSubmit)}
      onCancel={() => {
        setFormValues();
        onCancel();
      }}
      formDisabled={disabled}
      isSubmitDisabled={!canSubmitForm}
    >
      <Grid container spacing={3}>
        {/* Row 1 - Required Fields */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.patientAccountNumber)}
            label="Patient Account Number"
            required
            disabled={disabled || isEditMode}
            error={getFieldError(FormFields.patientAccountNumber)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.testCodeId)}
            label="Test Code ID"
            disabled={disabled}
            error={getFieldError(FormFields.testCodeId)}
          />
        </Grid>

        {/* Row 2 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.testCodeText)}
            label="Test Code Text"
            disabled={disabled}
            error={getFieldError(FormFields.testCodeText)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.placerOrder)}
            label="Placer Order"
            required
            disabled={disabled || isEditMode}
            error={getFieldError(FormFields.placerOrder)}
          />
        </Grid>

        {/* Row 3 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.fillerOrder)}
            label="Filler Order"
            disabled={disabled}
            error={getFieldError(FormFields.fillerOrder)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.placerGroup)}
            label="Placer Group"
            disabled={disabled}
            error={getFieldError(FormFields.placerGroup)}
          />
        </Grid>

        {/* Row 4 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.orderStatus)}
            label="Order Status"
            disabled={disabled}
            error={getFieldError(FormFields.orderStatus)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.quantity)}
            label="Quantity"
            type="number"
            disabled={disabled}
            error={getFieldError(FormFields.quantity)}
          />
        </Grid>

        {/* Row 5 */}
        <Grid item xs={12} md={6}>
          <Box>
            <DatePicker
              control={control}
              name={FormFields.dtOfTrnsaction}
              label="Date of Transaction"
              disabled={disabled}
              error={getFieldError(FormFields.dtOfTrnsaction)}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.orderingStaffId)}
            label="Ordering Staff ID"
            disabled={disabled}
            error={getFieldError(FormFields.orderingStaffId)}
          />
        </Grid>

        {/* Row 6 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.orderingStaffText)}
            label="Ordering Staff Text"
            disabled={disabled}
            error={getFieldError(FormFields.orderingStaffText)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.orderLocation)}
            label="Order Location"
            disabled={disabled}
            error={getFieldError(FormFields.orderLocation)}
          />
        </Grid>

        {/* Row 7 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.placerField)}
            label="Placer Field"
            disabled={disabled}
            error={getFieldError(FormFields.placerField)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.fillerField)}
            label="Filler Field"
            disabled={disabled}
            error={getFieldError(FormFields.fillerField)}
          />
        </Grid>

        {/* Row 8 */}
        <Grid item xs={12} md={6}>
          <InputField
            {...register(FormFields.diagService)}
            label="Diag Service"
            disabled={disabled}
            error={getFieldError(FormFields.diagService)}
          />
        </Grid>
      </Grid>
    </FormWrapper>
  );
};

Hl7OrderForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initValues: PropTypes.shape({}),
  disabled: PropTypes.bool,
  hideButtons: PropTypes.bool,
};

export default Hl7OrderForm;
